import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';
import { getBusinessHours } from '@/lib/api';
const DAY_KEYS = [
    'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
];
const JS_DAY_TO_KEY = [
    'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
];
// ─── Helpers ─────────────────────────────────────────────────────────────────
function minutesToTimeString(minutes, locale) {
    const date = new Date();
    date.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
    return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', hour12: false }).format(date);
}
function isOpenNow(day) {
    if (day.closed)
        return false;
    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    return nowMinutes >= day.openMinutes && nowMinutes < day.closeMinutes;
}
function todayKey() {
    return JS_DAY_TO_KEY[new Date().getDay()] ?? 'monday';
}
export function BusinessHoursWidget({ compact = false }) {
    const { t, i18n } = useTranslation('landing');
    const locale = i18n.language === 'en' ? 'en-GB' : 'fr-FR';
    const { data, isLoading, isError } = useQuery({
        queryKey: ['business-hours'],
        queryFn: getBusinessHours,
        staleTime: 60_000,
        refetchOnWindowFocus: true,
    });
    if (isLoading) {
        return (_jsxs("div", { className: "flex items-center gap-2 text-sm text-olive-400", children: [_jsx(Clock, { size: 14, strokeWidth: 1.5, "aria-hidden": "true" }), _jsx("span", { children: "\u2026" })] }));
    }
    if (isError || !data)
        return _jsx(_Fragment, {});
    const schedule = data.schedule;
    const today = todayKey();
    const todaySchedule = schedule[today];
    const openNow = isOpenNow(todaySchedule);
    if (compact) {
        return (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Clock, { size: 14, strokeWidth: 1.5, "aria-hidden": "true", className: "text-olive-400" }), _jsx("span", { className: "text-sm text-olive-700 dark:text-olive-300", children: todaySchedule.closed
                        ? t('hours.closedNow')
                        : openNow
                            ? `${t('hours.openNow')} · ${t('hours.closesAt', { time: minutesToTimeString(todaySchedule.closeMinutes, locale) })}`
                            : t('hours.opensAt', { time: minutesToTimeString(todaySchedule.openMinutes, locale) }) }), openNow && (_jsx("span", { className: "inline-flex items-center rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-emerald-700", children: t('hours.openNow') }))] }));
    }
    return (_jsxs("div", { className: "flex flex-col gap-2", children: [_jsxs("div", { className: "flex items-center gap-2 text-sm font-medium uppercase tracking-[0.08em] text-olive-500", children: [_jsx(Clock, { size: 14, strokeWidth: 1.5, "aria-hidden": "true" }), t('hours.title')] }), _jsx("dl", { className: "grid grid-cols-2 gap-x-4 gap-y-1.5", children: DAY_KEYS.map((key) => {
                    const day = schedule[key];
                    const isToday = key === today;
                    const openRow = isOpenNow(day);
                    return (_jsxs("div", { className: `contents${isToday ? ' [&>*]:font-semibold today-row-active' : ''}`, children: [_jsxs("dt", { className: `flex items-center gap-1.5 text-sm ${isToday
                                    ? 'rounded-sm bg-olive-100/60 pl-1.5 font-semibold text-olive-800 dark:bg-olive-800/40 dark:text-cream-50'
                                    : 'text-olive-500'}`, children: [isToday && (_jsx("span", { className: "inline-block h-1.5 w-1.5 rounded-full bg-olive-600 dark:bg-olive-400", "aria-hidden": "true" })), t(`hours.days${key.charAt(0).toUpperCase()}${key.slice(1)}`), isToday && (_jsxs("span", { className: "ml-1 text-[10px] font-normal text-olive-500", children: ["(", t('hours.today'), ")"] }))] }), _jsx("dd", { className: `text-sm ${isToday
                                    ? 'rounded-sm bg-olive-100/60 pr-1 font-semibold text-olive-800 dark:bg-olive-800/40 dark:text-cream-50'
                                    : 'text-olive-700'}`, style: { fontVariantNumeric: 'tabular-nums' }, children: day.closed ? (t('hours.closedNow')) : (_jsxs("span", { children: [minutesToTimeString(day.openMinutes, locale), "\u2013", minutesToTimeString(day.closeMinutes, locale), isToday && openRow && (_jsx("span", { className: "ml-2 inline-flex items-center rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700", children: t('hours.openNow') }))] })) })] }, key));
                }) })] }));
}
